/**
 * certs-under-domain.js — the certificates installed under one domain.
 *
 * The SSL tab of `/my/domains` used to show only the issuance pipeline, so a
 * certificate the platform had already installed — on any cluster, in any
 * datacenter — appeared nowhere. This is the pure half of the fix: it reads
 * `GET /cluster/certificates/registry?domain=<zone>` (cluster-api, owner
 * decision 2026-10-08) and turns it into something the panel can render
 * without deciding anything for itself.
 *
 * Four actions, and only four
 * ---------------------------
 * view · download · re-issue · **withdraw**. Withdraw stops the platform
 * serving and renewing a certificate (its registration and its Kubernetes
 * Secret go); the certificate stays valid at the certificate authority until
 * it expires, and re-issuing brings it back. **There is no "revoke" here and
 * there must never be one** — revoking at the authority cannot be undone, and
 * a key-compromise reason takes down every certificate sharing that key.
 *
 * Whether an action is allowed is the SERVER's answer (`row.actions`), never a
 * guess from the row's shape, and a closed action keeps its reason so the
 * panel can show it instead of quietly dropping the button.
 *
 * No `@/` imports — this module is covered by `node --test`.
 *
 * Author: Matew Makhatadze
 */

const REGISTRY_PATH = '/cluster/certificates/registry'

function str(v) { return String(v === null || v === undefined ? '' : v).trim() }

/** Lower-cased, trailing dot stripped, `*.` prefix removed. */
export function normalizeHost(v) {
  let s = str(v).toLowerCase().replace(/\.+$/, '')
  if (s.startsWith('*.')) s = s.slice(2)
  return s
}

/** Whole-label containment: `msda.ge` covers `api.msda.ge`, never `notmsda.ge`. */
export function underZone(name, zone) {
  const h = normalizeHost(name)
  const z = normalizeHost(zone)
  return !!h && !!z && (h === z || h.endsWith(`.${z}`))
}

// ── the request ─────────────────────────────────────────────────────────────

export function certificatesUrl(domain) {
  return `${REGISTRY_PATH}?domain=${encodeURIComponent(normalizeHost(domain))}`
}

/** Keep only the rows whose names actually fall under `zone`. */
export function filterUnderZone(rows, zone) {
  const z = normalizeHost(zone)
  if (!z) return []
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => certNames(row).some((n) => underZone(n, z)))
}

/**
 * Read every certificate under `domain`. Resolves
 * `{ ok, certificates, claim, filtered, missing, error }` and **never throws**.
 *
 * Two kinds of "this platform cannot do that yet", and they are different:
 *
 *   * `missing` — the registry route itself is absent (a 404 whose body is not
 *     a platform envelope). Nothing is listed.
 *   * `filtered: false` — the route answered but did **not** echo `domain`,
 *     which is how an older cluster-api behaves: it ignores the unknown query
 *     parameter and returns the caller's whole organisation instead. The rows
 *     are then narrowed to the zone HERE, so the tab never shows a certificate
 *     from another domain just because the server did not filter.
 *
 * Either way the panel says so in one calm line rather than showing a red
 * banner for a feature that is simply not deployed.
 */
export async function loadCertificatesUnderDomain(http, domain, { unwrap } = {}) {
  const take = unwrap || ((r) => (r && r.data && r.data.data) || (r && r.data) || null)
  try {
    const res = await http.get(certificatesUrl(domain))
    const data = take(res) || {}
    const rows = Array.isArray(data.certificates) ? data.certificates : []
    const filtered = normalizeHost(data.domain) === normalizeHost(domain)
    return {
      ok: true,
      // Belt and braces: the zone filter is applied here too, so a server that
      // ignored it cannot widen what the customer sees.
      certificates: filterUnderZone(rows, domain),
      claim: filtered ? (data.domain_claim || null) : null,
      filtered,
      missing: false,
      error: null
    }
  } catch (err) {
    const status = err && err.response && err.response.status
    const body = (err && err.response && err.response.data) || {}
    const isEnvelope = !!body && typeof body === 'object' &&
      (typeof body.success === 'boolean' || typeof body.message === 'string')
    if (status === 404 && !isEnvelope) {
      return { ok: false, certificates: [], claim: null, filtered: false, missing: true, error: null }
    }
    return {
      ok: false,
      certificates: [],
      claim: null,
      filtered: false,
      missing: false,
      error: body.message || (err && err.message) || 'Request failed'
    }
  }
}

/** The one line to show when the platform will not say what is under a domain. */
export const NO_CLAIM_NOTE =
  'The platform has no record of certificates under this domain for your organisation.'

export const ROUTE_MISSING_NOTE =
  'This platform does not list installed certificates yet. Certificates being issued are shown below.'

export const UNFILTERED_NOTE =
  'This platform cannot yet list certificates by domain, so only your organisation’s own ' +
  'certificates under this domain are shown.'

// ── names ──────────────────────────────────────────────────────────────────

/** Every hostname a row speaks for: its SANs, then the subject common name. */
export function certNames(row) {
  const out = []
  const seen = new Set()
  const push = (v) => {
    const h = normalizeHost(v)
    if (h && !seen.has(h)) { seen.add(h); out.push(h) }
  }
  ;(Array.isArray(row && row.sans) ? row.sans : []).forEach(push)
  push(commonNameOf(row && row.subject))
  return out
}

/** `CN=api.example.com,O=Example` → `api.example.com`. */
export function commonNameOf(subject) {
  const parts = str(subject).split(',')
  for (const part of parts) {
    const p = part.trim()
    if (p.toUpperCase().startsWith('CN=')) return p.slice(3).trim() || null
  }
  return null
}

/**
 * Names split for rendering: the first `max` shown, the rest behind a
 * disclosure. `logstack-tls` carries seven, which is why this exists.
 */
export function nameGroup(row, { max = 2 } = {}) {
  const names = certNames(row)
  const shown = names.slice(0, Math.max(1, max))
  const rest = names.slice(shown.length)
  return {
    shown,
    rest,
    total: names.length,
    moreLabel: rest.length ? `${rest.length} more ${rest.length === 1 ? 'name' : 'names'}` : null
  }
}

// ── expiry ─────────────────────────────────────────────────────────────────

export const EXPIRY_TONE = {
  EXPIRED: 'expired', URGENT: 'urgent', CAUTION: 'caution', CALM: 'calm', UNKNOWN: 'unknown'
}

/** Days remaining at or under which the chip reads as urgent. */
export const URGENT_DAYS = 7
/** …and under which it reads as worth planning for. */
export const CAUTION_DAYS = 30

function formatDay(iso) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * `{ days, date, label, tone, urgent }` for one row.
 *
 * `days_until_expiry` is the server's own arithmetic and is preferred; a row
 * with neither that nor a `not_after` is honestly unknown — never "fine".
 */
export function expiryState(row, { now = Date.now() } = {}) {
  const r = row || {}
  const date = r.not_after ? formatDay(r.not_after) : null
  let days = Number.isFinite(r.days_until_expiry) ? Math.trunc(r.days_until_expiry) : null
  if (days === null && r.not_after) {
    const at = new Date(r.not_after).getTime()
    if (!Number.isNaN(at)) days = Math.floor((at - now) / 86400000)
  }
  if (days === null) {
    return {
      days: null,
      date,
      tone: EXPIRY_TONE.UNKNOWN,
      urgent: false,
      label: 'Expiry not known yet'
    }
  }
  if (days < 0) {
    const ago = Math.abs(days)
    return {
      days,
      date,
      tone: EXPIRY_TONE.EXPIRED,
      urgent: true,
      label: ago === 0 ? 'Expired today' : `Expired ${ago} ${ago === 1 ? 'day' : 'days'} ago`
    }
  }
  const tone = days <= URGENT_DAYS
    ? EXPIRY_TONE.URGENT
    : days <= CAUTION_DAYS
      ? EXPIRY_TONE.CAUTION
      : EXPIRY_TONE.CALM
  const label = days === 0
    ? 'Expires today'
    : days === 1
      ? 'Expires tomorrow'
      : `Expires in ${days} days`
  return { days, date, tone, urgent: tone === EXPIRY_TONE.URGENT, label }
}

// ── mode and lane ──────────────────────────────────────────────────────────

const MODE_LABELS = {
  acme: 'Issued automatically',
  byo: 'Uploaded',
  observed: 'Observed',
  unmanaged: 'Unmanaged'
}

const MODE_NOTES = {
  acme: 'The platform orders and renews this certificate.',
  byo: 'Your organisation supplied this certificate.',
  observed: 'The platform found this certificate; whoever issued it renews it.',
  unmanaged: 'Nothing on the platform manages this certificate.'
}

/** `{ key, label, note }` — how this certificate came to exist. */
export function modeOf(row) {
  const key = str((row || {}).registration_mode || (row || {}).mode).toLowerCase()
  return {
    key: key || null,
    label: MODE_LABELS[key] || (key || 'Origin not known'),
    note: MODE_NOTES[key] || null
  }
}

/**
 * The lane, read off the REGISTRATION's hostnames — a registry row carries no
 * lane of its own. `null` when there is no registration to read, because a
 * guessed lane is worse than a quiet cell.
 */
export function laneOf(registration) {
  const hosts = (registration && Array.isArray(registration.hostnames))
    ? registration.hostnames
    : null
  if (!hosts || !hosts.length) return null
  let priv = 0
  hosts.forEach((h) => { if (!h || typeof h === 'string' || h.private !== false) priv += 1 })
  if (priv === hosts.length) return { key: 'private', label: 'Private' }
  if (priv === 0) return { key: 'public', label: 'Public' }
  return { key: 'mixed', label: 'Public and private' }
}

/**
 * One hostname's lane, from the registration that holds it. `null` when the
 * registration does not carry the name — a lane nobody recorded is not a lane
 * to display.
 */
export function laneForName(registration, name) {
  const hosts = (registration && Array.isArray(registration.hostnames))
    ? registration.hostnames
    : []
  const want = normalizeHost(name)
  for (const h of hosts) {
    const n = normalizeHost(typeof h === 'string' ? h : h && h.name)
    if (!n || n !== want) continue
    const isPrivate = typeof h === 'string' ? true : h.private !== false
    return {
      key: isPrivate ? 'private' : 'public',
      label: isPrivate ? 'Private' : 'Public',
      private: isPrivate
    }
  }
  return null
}

// ── actions ────────────────────────────────────────────────────────────────

export const ACTION_LABELS = {
  bundle: 'Download bundle',
  certificate: 'Download certificate',
  key: 'Download private key',
  reissue: 'Re-issue',
  withdraw: 'Withdraw'
}

/**
 * `{ allowed: [...], blocked: [{ action, label, reason }] }`.
 *
 * The server's `actions` block decides; a row that carries none (an older
 * cluster-api) offers nothing and says why once, rather than rendering buttons
 * that would 404.
 */
export function actionsOf(row) {
  const acts = (row && row.actions) || null
  const download = (acts && acts.download) || (row && row.download) || {}
  const allowed = []
  const blocked = []
  if (download.bundle) allowed.push('bundle')
  if (download.certificate) allowed.push('certificate')
  if (download.key) allowed.push('key')
  if (!download.bundle && download.key_reason) {
    blocked.push({ action: 'bundle', label: ACTION_LABELS.bundle, reason: download.key_reason })
  }
  ;['reissue', 'withdraw'].forEach((name) => {
    const entry = acts && acts[name]
    if (!entry) return
    if (entry.allowed) allowed.push(name)
    else blocked.push({ action: name, label: ACTION_LABELS[name], reason: entry.reason || null })
  })
  return { allowed, blocked }
}

/** Is `name` offered on this row? */
export function can(row, name) {
  return actionsOf(row).allowed.indexOf(name) !== -1
}

/**
 * What Withdraw will do, in the customer's own terms. Shown before the click,
 * every time — the act is reversible but it does take a live certificate out
 * of service.
 */
export function withdrawConfirm(row, { now = Date.now() } = {}) {
  const names = certNames(row)
  const primary = names[0] || (row && row.secret_name) || 'this certificate'
  const expiry = expiryState(row, { now })
  const tail = expiry.date
    ? `It stays valid at the certificate authority until ${expiry.date}.`
    : 'It stays valid at the certificate authority until it expires.'
  const secret = str((row || {}).secret_name)
  const what = secret
    ? `The platform stops serving and renewing it, and removes the Kubernetes secret ${secret}.`
    : 'The platform stops serving and renewing it, and removes its Kubernetes secret.'
  return {
    title: `Withdraw the certificate for ${primary}?`,
    body: `${what} ${tail}`,
    tail: 'You can re-issue it afterwards.',
    confirmLabel: 'Withdraw'
  }
}

/** `dev.denvstack.com.bundle.pem` — a name someone can find again later. */
export function downloadFilename(row, part) {
  const base = certNames(row)[0] || str((row || {}).secret_name) || 'certificate'
  return `${base}.${part === 'certificate' ? 'certificate' : part}.pem`
}

// ── grouping: datacenter → cluster/namespace → certificates ────────────────

export const UNKNOWN_PLACE = 'Datacenter not known'

function rowOrder(a, b) {
  const ea = expiryState(a)
  const eb = expiryState(b)
  const ka = ea.days === null ? Number.MAX_SAFE_INTEGER : ea.days
  const kb = eb.days === null ? Number.MAX_SAFE_INTEGER : eb.days
  if (ka !== kb) return ka - kb
  return str(a.secret_name).localeCompare(str(b.secret_name))
}

/**
 * `[{ datacenter, label, count, places: [{ cluster, namespace, label, rows }] }]`
 *
 * Soonest expiry first inside a place, so a certificate with five days left
 * leads its group. A row whose datacenter is unknown is grouped under
 * {@link UNKNOWN_PLACE} rather than silently folded into a real one.
 */
export function groupByPlace(rows) {
  const byDc = new Map()
  ;(Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row) return
    const dc = str(row.datacenter) || null
    const dcKey = dc || UNKNOWN_PLACE
    if (!byDc.has(dcKey)) byDc.set(dcKey, new Map())
    const places = byDc.get(dcKey)
    const cluster = str(row.cluster)
    const namespace = str(row.namespace)
    const key = `${cluster}/${namespace}`
    if (!places.has(key)) places.set(key, { cluster, namespace, label: key, rows: [] })
    places.get(key).rows.push(row)
  })
  const groups = []
  byDc.forEach((places, dcKey) => {
    const list = []
    places.forEach((place) => {
      place.rows.sort(rowOrder)
      list.push(place)
    })
    list.sort((a, b) => a.label.localeCompare(b.label))
    groups.push({
      datacenter: dcKey === UNKNOWN_PLACE ? null : dcKey,
      label: dcKey,
      count: list.reduce((n, p) => n + p.rows.length, 0),
      places: list
    })
  })
  groups.sort((a, b) => {
    if (!a.datacenter) return 1
    if (!b.datacenter) return -1
    return a.label.localeCompare(b.label)
  })
  return groups
}

/** `2 certificates · 1 expiring soon` — what the group header says. */
export function placeSummary(rows) {
  const list = Array.isArray(rows) ? rows : []
  const urgent = list.filter((r) => expiryState(r).urgent).length
  const parts = [`${list.length} ${list.length === 1 ? 'certificate' : 'certificates'}`]
  if (urgent) parts.push(`${urgent} needs attention`)
  return parts.join(' · ')
}

// ── joining the issuance pipeline to the row it belongs to ─────────────────

function targetKey(cluster, namespace, secret) {
  return `${normalizeHost(cluster)}|${normalizeHost(namespace)}|${normalizeHost(secret)}`
}

/**
 * Attach each registration to the installed row it belongs to, and keep the
 * registrations that have no row yet as rows of their own.
 *
 * A certificate mid-issue and the certificate on disk are the SAME
 * certificate seen from two ends, so the pipeline is shown ON its row — never
 * as a second list beside it. A registration the discovery sweep has not seen
 * yet (nothing installed) becomes a `pending: true` row so it is not lost.
 */
export function mergeRows(certificates, registrations, domain) {
  const rows = (Array.isArray(certificates) ? certificates : []).map((r) => ({ ...r }))
  const regs = Array.isArray(registrations) ? registrations : []
  const byId = new Map()
  const byTarget = new Map()
  regs.forEach((reg) => {
    if (!reg) return
    if (reg.id) byId.set(str(reg.id), reg)
    const t = reg.target || {}
    byTarget.set(targetKey(t.cluster, t.namespace, t.secret_name), reg)
  })
  const used = new Set()
  rows.forEach((row) => {
    const reg = (row.registration_id && byId.get(str(row.registration_id))) ||
      byTarget.get(targetKey(row.cluster, row.namespace, row.secret_name)) ||
      null
    row.registration = reg
    row.pending = false
    if (reg) used.add(reg)
  })
  regs.forEach((reg) => {
    if (!reg || used.has(reg)) return
    if (str(reg.state) === 'revoked') return
    const names = (Array.isArray(reg.hostnames) ? reg.hostnames : [])
      .map((h) => (typeof h === 'string' ? h : h && h.name))
      .filter(Boolean)
    if (domain && !names.some((n) => underZone(n, domain))) return
    const t = reg.target || {}
    rows.push({
      id: `pending:${str(reg.id)}`,
      pending: true,
      registration: reg,
      registration_id: reg.id || null,
      registration_state: reg.state || null,
      registration_mode: (reg.certificate && reg.certificate.mode) || null,
      cluster: t.cluster || null,
      namespace: t.namespace || null,
      secret_name: t.secret_name || null,
      datacenter: t.datacenter || null,
      sans: names,
      subject: null,
      issuer: (reg.certificate && reg.certificate.issuer_name) || null,
      not_after: (reg.certificate && reg.certificate.not_after) || null,
      days_until_expiry: null,
      secret_present: false,
      mode: (reg.certificate && reg.certificate.mode) || null,
      actions: null,
      download: null
    })
  })
  return rows
}

/** The line a not-yet-installed row carries instead of an expiry. */
export const PENDING_NOTE = 'Being issued — nothing installed yet.'

/** `issuer · expires 13 Oct 2026` for the row's fact line. */
export function issuerLine(row) {
  const issuer = str((row || {}).issuer)
  const expiry = expiryState(row)
  return [issuer || null, expiry.date ? `until ${expiry.date}` : null]
    .filter(Boolean).join(' · ')
}

export default {
  ACTION_LABELS,
  CAUTION_DAYS,
  EXPIRY_TONE,
  NO_CLAIM_NOTE,
  PENDING_NOTE,
  ROUTE_MISSING_NOTE,
  UNKNOWN_PLACE,
  URGENT_DAYS,
  actionsOf,
  can,
  certNames,
  certificatesUrl,
  commonNameOf,
  downloadFilename,
  expiryState,
  groupByPlace,
  issuerLine,
  laneForName,
  laneOf,
  loadCertificatesUnderDomain,
  mergeRows,
  modeOf,
  nameGroup,
  normalizeHost,
  placeSummary,
  underZone,
  withdrawConfirm
}
